<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <div v-if="!loading && campaign">
      <!-- Breadcrumb -->
      <breadcrumb style="margin-left: -15px">
        <breadcrumb-item>
          <a href="#" @click.prevent="goToList">Campaigns</a>
        </breadcrumb-item>
        <breadcrumb-item v-if="campaign.parent_campaign_id">
          <a href="#" @click.prevent="goToParent">Ongoing campaign</a>
        </breadcrumb-item>
        <breadcrumb-item>
          <span>{{ campaign.name }}</span>
        </breadcrumb-item>
      </breadcrumb>

      <div class="row">
        <div class="col-lg-12">
          <div class="cr-summary">
            <!-- ─── Header ────────────────────────────────────────────── -->
            <div class="gi-header" style="margin-bottom: 20px">
              <div class="gi-header__info">
                <h3 class="gi-header__title">{{ campaign.name }}</h3>
                <div class="gi-header__meta">
                  <badge :type="statusBadgeType">
                    <i v-if="campaign.status === 'sending'" class="el-icon-loading mr-1"></i>{{ statusBadgeLabel }}
                  </badge>
                  <span v-if="campaign.description" class="gi-header__meta-item">{{ campaign.description }}</span>
                </div>
              </div>
            </div>

            <info-hint v-if="campaign.status === 'sending'" variant="primary" style="width: 100%" class="mb-3">
              This campaign is still sending. Numbers update automatically every few seconds.
            </info-hint>

            <!-- ─── Hero stat cards ───────────────────────────────────── -->
            <div class="row gi-equal-height gi-equal-height--nowrap">
              <div class="col-lg-3 col-sm-6">
                <div class="gi-section gi-stat-card">
                  <div class="gi-stat-card__icon"><i class="tim-icons icon-send"></i></div>
                  <div class="gi-stat-card__content">
                    <span class="gi-section__label">Sent</span>
                    <div class="gi-stat-value">
                      {{ totals.sent.toLocaleString() }}
                      <small>of {{ totals.recipients.toLocaleString() }} {{ totals.recipients === 1 ? 'recipient' : 'recipients' }}</small>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-lg-3 col-sm-6">
                <div class="gi-section gi-stat-card">
                  <div class="gi-stat-card__icon"><i class="tim-icons icon-email-85"></i></div>
                  <div class="gi-stat-card__content">
                    <span class="gi-section__label">Delivered</span>
                    <div class="gi-stat-value">
                      {{ totals.delivered.toLocaleString() }}
                      <small>of {{ totals.sent.toLocaleString() }} sent</small>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-lg-3 col-sm-6">
                <div class="gi-section gi-stat-card">
                  <div class="gi-stat-card__icon"><i class="tim-icons icon-zoom-split"></i></div>
                  <div class="gi-stat-card__content">
                    <span class="gi-section__label">Open rate</span>
                    <div class="gi-stat-value">
                      {{ openRate }}
                      <small>{{ totals.unique_opens.toLocaleString() }} unique {{ totals.unique_opens === 1 ? 'open' : 'opens' }}</small>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-lg-3 col-sm-6">
                <div class="gi-section gi-stat-card">
                  <div class="gi-stat-card__icon"><i class="tim-icons icon-tap-02"></i></div>
                  <div class="gi-stat-card__content">
                    <span class="gi-section__label">Click rate</span>
                    <div class="gi-stat-value">
                      {{ clickRate }}
                      <small>{{ totals.clicks.toLocaleString() }} {{ totals.clicks === 1 ? 'click' : 'clicks' }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ─── Detail rows ───────────────────────────────────────── -->
            <div class="row gi-equal-height" style="margin-top: 10px">
              <div :class="detailColClass">
                <div class="gi-section">
                  <span class="gi-section__label gi-section__label--lg"><i class="tim-icons icon-paper"></i> Details</span>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Template</span>
                    <a
                      v-if="campaign.template_id"
                      class="gi-detail-row__value"
                      :href="`/mail/custom/template/${campaign.template_id}`"
                      target="_blank"
                      rel="noopener"
                    >{{ campaign.template_name || 'View template' }}</a>
                    <span v-else class="gi-detail-row__value">{{ campaign.template_name || '' }}</span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Subject</span>
                    <span class="gi-detail-row__value">{{ campaign.template_subject || '' }}</span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Audience</span>
                    <span class="gi-detail-row__value">{{ campaign.audience_name || 'Custom filter' }}</span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Created</span>
                    <span
                      class="gi-detail-row__value gi-detail-row__value--fit"
                      v-fit-text="{ min: 10, short: createdShort }"
                      :title="createdDisplay"
                    >{{ createdDisplay }}</span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Utm campaign</span>
                    <badge type="default" class="cr-utm-token" title="Copy to clipboard" @click.native="copyUtmToken">
                      campaign-{{ campaign.id }} <i class="tim-icons icon-single-copy-04"></i>
                    </badge>
                  </div>
                </div>
              </div>
              <div :class="detailColClass">
                <div class="gi-section">
                  <span class="gi-section__label gi-section__label--lg"><i class="tim-icons icon-sound-wave"></i> Engagement</span>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Total opens</span>
                    <span class="gi-detail-row__value">{{ totals.opens.toLocaleString() }}</span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Total clicks</span>
                    <span class="gi-detail-row__value">{{ totals.clicks.toLocaleString() }}</span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Unique clicks</span>
                    <span class="gi-detail-row__value">{{ (totals.unique_clicks || 0).toLocaleString() }}</span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Unsubscribes</span>
                    <span class="gi-detail-row__value" :class="totals.unsubscribes > 0 ? 'text-warning' : ''">{{ totals.unsubscribes.toLocaleString() }}</span>
                  </div>
                </div>
              </div>
              <div :class="detailColClass">
                <div class="gi-section cr-deliverability">
                  <span class="gi-section__label gi-section__label--lg"><i class="tim-icons icon-alert-circle-exc"></i> Deliverability</span>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Failed</span>
                    <span class="gi-detail-row__value" :class="totals.failed > 0 ? 'gi-detail-row__value--danger' : ''">
                      {{ totals.failed.toLocaleString() }}
                      <span v-if="hasAnyStatDownload" class="cr-stat-slot">
                        <base-button v-if="hasDownloadPermission && totals.failed > 0" icon class="btn-link text-white m-0 cr-stat-download" :disabled="exporting" title="Download this list" @click="downloadStat('failed')">
                          <i :class="exporting ? 'el-icon-loading' : 'tim-icons icon-cloud-download-93'"></i>
                        </base-button>
                      </span>
                    </span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">
                      Skipped
                      <el-popover trigger="hover" placement="right" width="280">
                        <div style="word-break: normal; text-align: left">
                          Skipped recipients either opted out of marketing email between scheduling and sending, were left unsent because the campaign was cancelled, or were stopped by the automatic reputation guard when bounce or complaint rates ran too high. Addresses suppressed by list hygiene are counted here too; see the Suppressed row.
                        </div>
                        <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                      </el-popover>
                    </span>
                    <span class="gi-detail-row__value">
                      {{ totals.skipped.toLocaleString() }}
                      <span v-if="hasAnyStatDownload" class="cr-stat-slot">
                        <base-button v-if="hasDownloadPermission && totals.skipped > 0" icon class="btn-link text-white m-0 cr-stat-download" :disabled="exporting" title="Download this list" @click="downloadStat('skipped')">
                          <i :class="exporting ? 'el-icon-loading' : 'tim-icons icon-cloud-download-93'"></i>
                        </base-button>
                      </span>
                    </span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">
                      Suppressed
                      <el-popover trigger="hover" placement="right" width="300">
                        <div style="word-break: normal; text-align: left">
                          Addresses skipped before sending because they previously bounced, were previously marked as spam, are not valid addresses, use a disposable email service, or belong to a domain that cannot receive mail. They are included in the skipped count. Filter the recipient list by reason to see each one.
                        </div>
                        <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                      </el-popover>
                    </span>
                    <span class="gi-detail-row__value" :class="totals.suppressed > 0 ? 'gi-detail-row__value--danger' : ''">
                      {{ (totals.suppressed || 0).toLocaleString() }}
                      <span v-if="hasAnyStatDownload" class="cr-stat-slot">
                        <base-button v-if="hasDownloadPermission && totals.suppressed > 0" icon class="btn-link text-white m-0 cr-stat-download" :disabled="exporting" title="Download this list" @click="downloadStat('suppressed')">
                          <i :class="exporting ? 'el-icon-loading' : 'tim-icons icon-cloud-download-93'"></i>
                        </base-button>
                      </span>
                    </span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Bounces</span>
                    <span class="gi-detail-row__value" :class="totals.bounces > 0 ? 'gi-detail-row__value--danger' : ''">
                      {{ totals.bounces.toLocaleString() }}
                      <span v-if="hasAnyStatDownload" class="cr-stat-slot">
                        <base-button v-if="hasDownloadPermission && totals.bounces > 0" icon class="btn-link text-white m-0 cr-stat-download" :disabled="exporting" title="Download this list" @click="downloadStat('bounces')">
                          <i :class="exporting ? 'el-icon-loading' : 'tim-icons icon-cloud-download-93'"></i>
                        </base-button>
                      </span>
                    </span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Complaints</span>
                    <span class="gi-detail-row__value" :class="totals.complaints > 0 ? 'gi-detail-row__value--danger' : ''">
                      {{ totals.complaints.toLocaleString() }}
                      <span v-if="hasAnyStatDownload" class="cr-stat-slot">
                        <base-button v-if="hasDownloadPermission && totals.complaints > 0" icon class="btn-link text-white m-0 cr-stat-download" :disabled="exporting" title="Download this list" @click="downloadStat('complaints')">
                          <i :class="exporting ? 'el-icon-loading' : 'tim-icons icon-cloud-download-93'"></i>
                        </base-button>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <div :class="detailColClass">
                <div class="gi-section">
                  <span class="gi-section__label gi-section__label--lg"><i class="tim-icons icon-money-coins"></i> Revenue</span>
                  <template v-if="attribution.available">
                    <div class="gi-detail-row">
                      <span class="gi-detail-row__label">Attributed purchases</span>
                      <span class="gi-detail-row__value">
                        <a v-if="attribution.purchases > 0" href="#" class="text-primary" @click.prevent="openPurchasesDrawer">{{ attribution.purchases.toLocaleString() }} <i class="tim-icons icon-minimal-right" style="font-size: 10px;"></i></a>
                        <template v-else>{{ attribution.purchases.toLocaleString() }}</template>
                      </span>
                    </div>
                    <div class="gi-detail-row">
                      <span class="gi-detail-row__label">Attributed revenue</span>
                      <span class="gi-detail-row__value" :class="attribution.revenue > 0 ? 'gi-detail-row__value--success' : ''">{{ attributedRevenue }}</span>
                    </div>
                    <div class="gi-detail-row">
                      <span class="gi-detail-row__label">Customers who purchased</span>
                      <span class="gi-detail-row__value">{{ attribution.customers.toLocaleString() }}</span>
                    </div>
                    <p class="cr-attribution-note mb-0"><small>Counts purchases made within 7 days of a recipient's first click on this campaign.</small></p>
                    <p v-if="attribution.scoped" class="cr-attribution-note mb-0"><small>Limited to purchases in groups you can view.</small></p>
                  </template>
                  <p v-else class="cr-attribution-note mb-0"><small>Available after the campaign finishes sending.</small></p>
                </div>
              </div>
              <div v-if="promo" :class="detailColClass">
                <div class="gi-section">
                  <span class="gi-section__label gi-section__label--lg"><i class="tim-icons icon-tag"></i> Promo codes</span>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Issued</span>
                    <span class="gi-detail-row__value">{{ promo.issued.toLocaleString() }}</span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Redeemed</span>
                    <span class="gi-detail-row__value" :class="promo.redeemed > 0 ? 'gi-detail-row__value--success' : ''">{{ promo.redeemed.toLocaleString() }}</span>
                  </div>
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Expired</span>
                    <span class="gi-detail-row__value">{{ promo.expired.toLocaleString() }}</span>
                  </div>
                  <p class="cr-attribution-note mb-0"><small>Codes are single use and valid only in each customer's birthday window.</small></p>
                </div>
              </div>
            </div>
          </div>

          <!-- ─── Recipients ────────────────────────────────────────────── -->
          <card class="cr-recipients">
            <template slot="header">
              <div class="d-flex align-items-center justify-content-between">
                <h4 class="card-title mb-0">Recipients</h4>
                <base-button
                  v-if="hasDownloadPermission"
                  icon
                  class="btn-link text-white m-0"
                  :disabled="exporting"
                  @click="downloadCsv"
                >
                  <i :class="exporting ? 'el-icon-loading' : 'tim-icons icon-cloud-download-93'"></i>
                </base-button>
              </div>
            </template>

            <info-hint v-if="recipientsScoped" variant="primary" style="width: 100%" class="mb-3">
              Showing only recipients from groups you can view. The totals above count every recipient.
            </info-hint>

            <filter-bar
              :filters="recipientFilterSchema"
              :initial-value="recipientFilters"
              :result-count="recipientTotal"
              :loading="recipientsRefreshing"
              :per-page="perPage"
              @change="onRecipientFiltersChanged"
              @update:perPage="onPerPageChange"
            />

            <b-table
              :items="recipients"
              :fields="recipientFields"
              responsive
            >
              <template v-slot:cell(name)="row">
                <a
                  v-if="row.item.end_user_id && hasCustomerViewPermission"
                  :href="`/customers/details?id=${row.item.end_user_id}`"
                  target="_blank"
                  rel="noopener"
                >{{ recipientName(row.item) }}</a>
                <span v-else class="text-white">{{ recipientName(row.item) }}</span>
              </template>
              <template v-slot:cell(status)="row">
                <span :class="['badge', 'badge-' + recipientStatusColor(row.item.status)]">{{ row.item.status }}</span>
                <span v-if="row.item.bounced" class="badge badge-warning ml-1">bounced</span>
                <span v-if="row.item.complained" class="badge badge-danger ml-1">marked as spam</span>
              </template>
              <template v-slot:cell(promo_code)="row">
                <span class="text-white">{{ row.item.promo_code || '' }}</span>
              </template>
              <template v-slot:cell(promo_redeemed)="row">
                <span v-if="row.item.promo_redeemed" class="badge badge-success">redeemed</span>
                <span v-else-if="row.item.promo_code"></span>
              </template>
              <template v-slot:cell(error)="row">
                <small>{{ reasonLabel(row.item.error) }}</small>
              </template>
              <template v-slot:cell(sent_at)="row">
                <small>{{ row.item.sent_at ? displayDateTime(row.item.sent_at) : '-' }}</small>
              </template>
            </b-table>
            <div class="row" v-if="recipientTotal > perPage">
              <div class="col d-flex justify-content-center">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="recipientTotal"
                  :per-page="perPage"
                  @input="fetchRecipients"
                ></b-pagination>
              </div>
            </div>
          </card>
        </div>
      </div>
    </div>
    <attributed-purchases-drawer
      :show="purchasesDrawerOpen"
      :rows="purchases"
      :loading="purchasesLoading"
      :page="purchasesPage"
      :per-page="PURCHASES_PER_PAGE"
      :total="purchasesTotal"
      :can-download="hasDownloadPermission"
      :exporting="purchasesExporting"
      @close="purchasesDrawerOpen = false"
      @update:page="onPurchasesPageChange"
      @download="downloadPurchasesCsv"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { BTable, BPagination } from 'bootstrap-vue'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat'
import { Badge } from 'src/components'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue'
import BreadcrumbItem from '@/components/Breadcrumb/BreadcrumbItem.vue'
import AttributedPurchasesDrawer from './AttributedPurchasesDrawer.vue'
import { REASON_LABELS, SUPPRESSION_CODES, reasonLabel } from '@/util/campaignSuppressionReasons'

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const loading = ref(true)
const exporting = ref(false)
const campaign = ref<any>(null)
const totals = ref<any>({
  recipients: 0, sent: 0, failed: 0, skipped: 0,
  delivered: 0, opens: 0, unique_opens: 0, clicks: 0, unique_clicks: 0,
  bounces: 0, complaints: 0, unsubscribes: 0, suppressed: 0
})
const attribution = ref<any>({ model: 'click_7d', available: false, purchases: 0, revenue: 0, customers: 0 })
// Attributed purchases drawer: lazy, fetched on first open, paged server side
const purchasesDrawerOpen = ref(false)
const purchases = ref<any[]>([])
const purchasesTotal = ref(0)
const purchasesLoading = ref(false)
const purchasesPage = ref(1)
const PURCHASES_PER_PAGE = 25
const purchasesExporting = ref(false)
const recipients = ref<any[]>([])
const recipientTotal = ref(0)
const recipientsScoped = ref(false)
const recipientsRefreshing = ref(false)
const currentPage = ref(1)
const perPage = ref(25)
const recipientFilters = ref<Record<string, any>>({ search: '', status: [], issue: [], reason: [] })
let pollTimer: ReturnType<typeof setInterval> | null = null

const REASON_OPTIONS = Object.entries(REASON_LABELS).map(([value, label]) => ({ label, value }))

// Deliverability rows download the matching recipients as CSV. Failed,
// Skipped and Suppressed are recipient statuses; Bounces and Complaints are
// event flags, so those files list distinct recipients and can hold fewer rows
// than the event count shown.
const EMPTY_FILTERS = { search: '', status: [] as string[], issue: [] as string[], reason: [] as string[] }
const STAT_DOWNLOADS: Record<string, { filters: Record<string, any>; suffix: string }> = {
  failed: { filters: { ...EMPTY_FILTERS, status: ['failed'] }, suffix: 'failed' },
  skipped: { filters: { ...EMPTY_FILTERS, status: ['skipped'] }, suffix: 'skipped' },
  suppressed: { filters: { ...EMPTY_FILTERS, status: ['skipped'], reason: SUPPRESSION_CODES }, suffix: 'suppressed' },
  bounces: { filters: { ...EMPTY_FILTERS, issue: ['bounced'] }, suffix: 'bounced' },
  complaints: { filters: { ...EMPTY_FILTERS, issue: ['complained'] }, suffix: 'complaints' }
}
function downloadStat(key: string) {
  const def = STAT_DOWNLOADS[key]
  if (def) downloadCsv(def.filters, def.suffix)
}

const recipientFilterSchema = [
  { key: 'search', type: 'text', label: 'Name or email', placeholder: 'Search recipients', primary: true },
  {
    key: 'status',
    type: 'multiselect',
    label: 'Status',
    primary: true,
    options: [
      { label: 'Sent', value: 'sent' },
      { label: 'Failed', value: 'failed' },
      { label: 'Skipped', value: 'skipped' },
      { label: 'Pending', value: 'pending' },
      { label: 'Processing', value: 'processing' }
    ]
  },
  // Post-send problems reported by the mailbox provider; any selected value matches
  {
    key: 'issue',
    type: 'multiselect',
    label: 'Delivery issue',
    primary: true,
    options: [
      { label: 'Bounced', value: 'bounced' },
      { label: 'Marked as spam', value: 'complained' }
    ]
  },
  { key: 'reason', type: 'multiselect', label: 'Reason', primary: true, options: REASON_OPTIONS }
]

const storedPermissions = (() => {
  try {
    return JSON.parse(localStorage.getItem('permissions') || '[]') as string[]
  } catch {
    return []
  }
})()
const hasDownloadPermission = storedPermissions.includes('download_data')
const hasCustomerViewPermission = storedPermissions.includes('customer_view')

// The download slot beside each Deliverability number keeps the numbers in
// one column; it is only rendered when at least one row has a download, so a
// card with nothing to download keeps its numbers flush with the other cards
const hasAnyStatDownload = computed(() => hasDownloadPermission
  && ['failed', 'skipped', 'suppressed', 'bounces', 'complaints'].some((k) => Number(totals.value?.[k]) > 0))

const recipientFields = computed(() => {
  const fields: Array<{ key: string; label: string }> = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'status', label: 'Status' }
  ]
  if (campaign.value?.promo_template_id) {
    fields.push({ key: 'promo_code', label: 'Promo code' }, { key: 'promo_redeemed', label: 'Redeemed' })
  }
  fields.push({ key: 'error', label: 'Error' }, { key: 'sent_at', label: 'Sent' })
  return fields
})

// Five detail cards share the row when a campaign issues promo codes; four otherwise
const detailColClass = computed(() => (promo.value ? 'col-xl col-lg-4 col-md-6' : 'col-xl-3 col-lg-6'))
const promo = ref<{ issued: number; redeemed: number; expired: number } | null>(null)
function goToParent() {
  if (campaign.value?.parent_campaign_id) proxy.$router.push(`/marketing/campaigns/${campaign.value.parent_campaign_id}/report`)
}

function sendTimeDisplay(c: any): string {
  const hm = String(c.send_time || '09:00').slice(0, 5)
  const group = JSON.parse(localStorage.getItem('group') || '{}')
  const d = new Date(`2000-01-01T${hm}:00`)
  return Number.isNaN(d.getTime()) ? hm : formatGroupTime(d.toISOString()) || hm
}

const STATUS_BADGE_TYPES: Record<string, string> = {
  draft: 'default',
  scheduled: 'info',
  sending: 'warning',
  sent: 'success',
  cancelled: 'default',
  failed: 'danger'
}
const statusBadgeType = computed(() => STATUS_BADGE_TYPES[campaign.value?.status] || 'default')

// The send/schedule time lives inside the status badge; header dates honor
// the group's show day of week setting (formatGroupDate default)
const statusBadgeLabel = computed(() => {
  const c = campaign.value
  if (!c) return ''
  if (c.recurrence === 'daily') {
    if (c.status === 'scheduled') return `active, runs daily at ${sendTimeDisplay(c)}`
    if (c.status === 'sent') return c.sent_at ? `finished ${formatGroupDate(c.sent_at)}` : 'finished'
    if (c.status === 'cancelled') return 'stopped'
  }
  if (c.status === 'sent' && c.sent_at) return `sent ${displayDateTime(c.sent_at)}`
  if (c.status === 'scheduled' && c.scheduled_at) return `scheduled for ${displayDateTime(c.scheduled_at)}`
  return c.status
})

function displayDateTime(value: string) {
  return `${formatGroupDate(value)}, ${formatGroupTime(value)}`
}

// Created row: full form with the weekday when it fits, shorter form without it otherwise
const createdDisplay = computed(() => (campaign.value?.created_at ? displayDateTime(campaign.value.created_at) : ''))
const createdShort = computed(() => (campaign.value?.created_at
  ? `${formatGroupDate(campaign.value.created_at, { dayOfWeek: false })}, ${formatGroupTime(campaign.value.created_at)}`
  : ''))

const recipientStatusColor = (status: string) => {
  if (status === 'sent') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'skipped') return 'default'
  return 'info'
}

function recipientName(r: any) {
  return `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email
}

// Mailchimp-style headline rates: opens and clicks over delivered
const openRate = computed(() => {
  const t = totals.value
  return t.delivered > 0 ? `${Math.round((t.unique_opens / t.delivered) * 100)}%` : '0%'
})
const clickRate = computed(() => {
  const t = totals.value
  return t.delivered > 0 ? `${Math.round(((t.unique_clicks || 0) / t.delivered) * 100)}%` : '0%'
})

const attributedRevenue = computed(() => {
  const gs = getGroupSettings()
  return formatCurrency(attribution.value.revenue, {
    currency: gs.currency,
    currencyFormat: gs.currency_format,
    numberFormat: gs.number_format
  })
})

function formatDateTime(value: string) {
  const date = formatGroupDate(value, { dayOfWeek: false })
  const time = formatGroupTime(value)
  return `${date}, ${time}`
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function recipientQueryFor(filters: Record<string, any>, count: number, offset: number) {
  let query = `count=${count}&offset=${offset}`
  if (filters.search) query += `&search=${encodeURIComponent(filters.search)}`
  if (filters.status && filters.status.length) {
    query += `&status=${filters.status.join(',')}`
  }
  if (filters.issue && filters.issue.length) query += `&issue=${filters.issue.join(',')}`
  if (filters.reason && filters.reason.length) {
    query += `&reason=${filters.reason.join(',')}`
  }
  return query
}

function recipientQuery(count: number, offset: number) {
  return recipientQueryFor(recipientFilters.value, count, offset)
}

async function fetchRecipients() {
  recipientsRefreshing.value = true
  try {
    const config = await authConfig()
    const offset = (currentPage.value - 1) * perPage.value
    const url = `${baseUrl}/email-campaigns/${proxy.$route.params.id}/recipients?${recipientQuery(perPage.value, offset)}`
    const response = await axios.get(url, config)
    recipients.value = response.data.recipients || []
    recipientTotal.value = response.data.total_rows || 0
    recipientsScoped.value = response.data.scoped === true
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    proxy.$notify({ type: 'danger', message: 'Could not load the recipient list', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    recipientsRefreshing.value = false
  }
}

function onRecipientFiltersChanged(values: Record<string, any>) {
  recipientFilters.value = values
  currentPage.value = 1
  fetchRecipients()
}

function onPerPageChange(value: number) {
  perPage.value = value
  currentPage.value = 1
  fetchRecipients()
}

async function fetchStats(showLoader = true) {
  if (showLoader) loading.value = true
  try {
    const config = await authConfig()
    const url = `${baseUrl}/email-campaigns/${proxy.$route.params.id}/stats`
    const response = await axios.get(url, config)
    campaign.value = response.data.campaign
    totals.value = response.data.totals
    attribution.value = response.data.attribution || attribution.value
    promo.value = response.data.promo || null

    // Poll while sending so the report tracks progress live
    if (campaign.value.status === 'sending' && !pollTimer) {
      pollTimer = setInterval(() => { fetchStats(false); fetchRecipients() }, 10000)
    } else if (campaign.value.status !== 'sending' && pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    proxy.$notify({ type: 'danger', message: 'Could not load the campaign report', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

function purchasesUrl(count: number, offset: number) {
  return `${baseUrl}/email-campaigns/${proxy.$route.params.id}/stats?detail=purchases&count=${count}&offset=${offset}`
}

async function fetchPurchases() {
  purchasesLoading.value = true
  try {
    const config = await authConfig()
    const offset = (purchasesPage.value - 1) * PURCHASES_PER_PAGE
    const response = await axios.get(purchasesUrl(PURCHASES_PER_PAGE, offset), config)
    purchases.value = response.data.purchases || []
    purchasesTotal.value = Number(response.data.total_rows) || 0
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: 'Could not load the purchases list', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    purchasesLoading.value = false
  }
}

function openPurchasesDrawer() {
  purchasesDrawerOpen.value = true
  purchasesPage.value = 1
  fetchPurchases()
}

function onPurchasesPageChange(page: number) {
  purchasesPage.value = page
  fetchPurchases()
}

// Full list, not just the current page; same CSV conventions as downloadCsv
async function downloadPurchasesCsv() {
  purchasesExporting.value = true
  try {
    const config = await authConfig()
    const EXPORT_PAGE = 10000
    const rows: any[] = []
    let offset = 0
    let total = Infinity
    while (offset < total) {
      const response = await axios.get(purchasesUrl(EXPORT_PAGE, offset), config)
      const page = response.data.purchases || []
      rows.push(...page)
      total = Number(response.data.total_rows) || 0
      if (page.length < EXPORT_PAGE) break
      offset += EXPORT_PAGE
    }

    let csv = 'Customer,Order,Purchased,Paid,Refunded,Clicked\n'
    rows.forEach((r: any) => {
      csv += [
        escapeCsvValue(`${r.first_name || ''} ${r.last_name || ''}`.trim()),
        escapeCsvValue(r.order_number || ''),
        escapeCsvValue(r.timestamp ? formatDateTime(r.timestamp) : ''),
        r.paid,
        r.refunded,
        escapeCsvValue(r.first_click ? formatDateTime(r.first_click) : '')
      ].join(',')
      csv += '\n'
    })

    const anchor = document.createElement('a')
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    anchor.target = '_blank'
    anchor.download = `campaign-${proxy.$route.params.id}-purchases.csv`
    anchor.click()
  } catch (err) {
    proxy.$notify({ type: 'danger', message: 'Could not export the purchases list', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    purchasesExporting.value = false
  }
}

// Same CSV conventions as the transactions export: quote-escape, data URI
function escapeCsvValue(value: any) {
  if (typeof value === 'string') {
    value = value.replace(/"/g, '""')
    if (value.includes(',') || value.includes('\n')) {
      value = `"${value}"`
    }
  }
  return value ?? ''
}

// Exports the table's current filters by default; the Deliverability rows pass
// their own filter set and a file name suffix.
async function downloadCsv(override: Record<string, any> | null = null, suffix = 'recipients') {
  exporting.value = true
  try {
    const config = await authConfig()
    const filters = override ?? recipientFilters.value
    // The recipients endpoint serves at most 10,000 rows per request; page
    // through so a large campaign exports in full.
    const EXPORT_PAGE = 10000
    const rows: any[] = []
    let offset = 0
    let total = Infinity
    while (offset < total) {
      const url = `${baseUrl}/email-campaigns/${proxy.$route.params.id}/recipients?${recipientQueryFor(filters, EXPORT_PAGE, offset)}`
      const response = await axios.get(url, config)
      const page = response.data.recipients || []
      rows.push(...page)
      total = Number(response.data.total_rows) || 0
      if (page.length < EXPORT_PAGE) break
      offset += EXPORT_PAGE
    }

    const withPromo = !!campaign.value?.promo_template_id
    let csv = 'Name,Email,Status,Marked as spam,Bounced,Error,Sent' + (withPromo ? ',Promo code,Redeemed' : '') + '\n'
    rows.forEach((r: any) => {
      const cells = [
        escapeCsvValue(recipientName(r)),
        escapeCsvValue(r.email),
        r.status,
        r.complained ? 'yes' : 'no',
        r.bounced ? 'yes' : 'no',
        escapeCsvValue(r.error ? reasonLabel(r.error) : ''),
        escapeCsvValue(r.sent_at ? formatDateTime(r.sent_at) : '')
      ]
      if (withPromo) cells.push(escapeCsvValue(r.promo_code || ''), r.promo_redeemed ? 'yes' : 'no')
      csv += cells.join(',')
      csv += '\n'
    })

    const anchor = document.createElement('a')
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    anchor.target = '_blank'
    anchor.download = `campaign-${proxy.$route.params.id}-${suffix}.csv`
    anchor.click()
  } catch (err) {
    proxy.$notify({ type: 'danger', message: 'Could not export the recipient list', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    exporting.value = false
  }
}

onMounted(() => { fetchStats(); fetchRecipients() })
onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
})

function goToList() {
  proxy.$router.push('/marketing/campaigns')
}

function copyUtmToken() {
  navigator.clipboard.writeText(`campaign-${campaign.value.id}`)
  proxy.$notify({ type: 'success', message: 'Copied to clipboard', icon: 'tim-icons icon-check-2' })
}
</script>

<script lang="ts">
import { Popover } from 'element-ui'

export default {
  name: 'campaign-report',
  components: {
    [Popover.name]: Popover
  }
}
</script>

<style>
/* The report reuses the game instance summary's design system (header tile,
   glowing stat cards, section panels, detail rows) so the two summary pages
   read identically. The summary block intentionally has no outer card: the
   gi sections carry their own surfaces, so they sit directly on the page. */
@import '../../Games/GameHistory/game-instance.css';

.cr-summary {
  margin-bottom: 30px;
}

/* Surfaces match the recipients card below (same card background), keeping
   the gi drop shadows. Light mode falls back to the gi white surface. */
.cr-summary .gi-section {
  background: #22233a;
}
.white-content .cr-summary .gi-section {
  background: #fff;
}

/* The recipients card shares the gi shadow treatment */
.cr-recipients.card {
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.5),
    0 1px 3px rgba(0, 0, 0, 0.3);
}
.white-content .cr-recipients.card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* Breathing room between section headers and their first detail row */
.cr-summary .gi-section__label--lg {
  margin-bottom: 12px;
}

/* The utm token badge copies on click */
.cr-utm-token {
  cursor: pointer;
  text-transform: none;
  margin-bottom: 0;
}
.cr-utm-token i {
  font-size: 10px;
  margin-left: 2px;
}

/* Per-stat download control beside a Deliverability value. The slot is
   always rendered so every number ends at the same column, and the icon
   button is shrunk to its glyph so it never changes the row height. */
.cr-summary .cr-deliverability .gi-detail-row__value {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
}
.cr-summary .cr-stat-slot {
  display: inline-flex;
  justify-content: flex-end;
  width: 26px;
  flex: 0 0 26px;
}
.cr-summary .cr-stat-download.btn.btn-icon {
  width: 18px;
  min-width: 18px;
  height: 18px;
  padding: 0;
  margin: 0;
  font-size: 13px;
  line-height: 1;
}

/* Created value stays on one line: it takes the remaining row width so the
   global v-fit-text directive can measure and shrink it (a plain detail row
   value sizes to its content and wraps) */
.cr-summary .gi-detail-row__value--fit {
  flex: 1 1 auto;
  min-width: 0;
  margin-left: 12px;
}

/* Attribution model note under the revenue rows */
.cr-attribution-note {
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.75);
}
.white-content .cr-attribution-note {
  color: rgba(0, 0, 0, 0.7);
}

/* The of X recipients / of X sent sub-labels read at full strength */
.cr-summary .gi-stat-value small {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}
.white-content .cr-summary .gi-stat-value small {
  color: rgba(0, 0, 0, 0.55);
}

/* The badge now carries the send time; let it wrap instead of overflowing
   on narrow screens */
.cr-summary .gi-header .badge {
  white-space: normal;
  text-align: left;
}

/* Stacked full-width stat cards on small screens: undo the vw-clamped
   shrink (sized for four-across desktop) so type stays readable */
@media (max-width: 991px) {
  .cr-summary .gi-stat-card .gi-section__label {
    font-size: 13px;
  }
  .cr-summary .gi-stat-value {
    font-size: 22px;
  }
  .cr-summary .gi-stat-card__icon {
    width: 42px;
    height: 42px;
  }
  .cr-summary .gi-stat-card__icon i {
    font-size: 19px;
  }
}
</style>
